import { useEffect, useState, useCallback } from "react";
import { Check, X, Edit3, Save, ChevronRight, Mail, Briefcase, Users, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const TABS = [
  { key: "status", label: "Project Status" },
  { key: "investors", label: "Investors" },
  { key: "inquiries", label: "Inquiries" },
  { key: "partners", label: "Partner Applications" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("status");

  return (
    <div data-testid="page-admin" className="bg-[#F9F9F6] pt-32 md:pt-36 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6 items-end pb-10 border-b border-[#1A1A1A]">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow text-[#5C2018]">LRG Holdings · Admin</p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mt-4">
              Project execution dashboard.
            </h1>
            <p className="text-sm text-[#666666] mt-3">{user?.email} · Full access</p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <p className="eyebrow">Project</p>
            <p className="font-serif text-xl mt-1">LRG Leather Botswana</p>
            <p className="text-xs text-[#666666] mt-1">Lobatse · SEZA</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-0 mt-10 border-b border-[#E5E5E0]">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 text-[12px] uppercase tracking-[0.18em] font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#5C2018] text-[#1A1A1A]" : "border-transparent text-[#666666] hover:text-[#1A1A1A]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "status" && <StatusPanel />}
          {tab === "investors" && <InvestorsPanel />}
          {tab === "inquiries" && <InquiriesPanel />}
          {tab === "partners" && <PartnersPanel />}
        </div>
      </div>
    </div>
  );
}

// ----------------- STATUS -----------------
function StatusPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/project-status").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-status-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
        {loading ? (
          <p className="p-8 bg-[#F9F9F6] text-sm text-[#666666]">Loading…</p>
        ) : (
          items.map((s) => (
            <StatusRow key={s.id} item={s} editing={editingId === s.id} setEditing={setEditingId} onSaved={load} />
          ))
        )}
      </div>
    </div>
  );
}

function StatusRow({ item, editing, setEditing, onSaved }) {
  const [draft, setDraft] = useState(item);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!editing) setDraft(item);
  }, [editing, item]);

  const change = (k) => (e) => setDraft({ ...draft, [k]: k === "progress" ? Number(e.target.value) : e.target.value });

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/project-status/${item.id}`, {
        title: draft.title,
        status: draft.status,
        progress: Number(draft.progress),
        note: draft.note,
        target_date: draft.target_date,
      });
      setEditing(null);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F9F9F6] p-6 md:p-8" data-testid={`admin-status-row-${item.id}`}>
      <div className="flex items-center justify-between">
        <p className="eyebrow text-[#5C2018]">{item.category}</p>
        {editing ? (
          <button
            onClick={save}
            disabled={saving}
            data-testid={`admin-status-save-${item.id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#5C2018] text-[#F9F9F6] text-[10px] uppercase tracking-[0.2em]"
          >
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        ) : (
          <button
            onClick={() => setEditing(item.id)}
            data-testid={`admin-status-edit-${item.id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#1A1A1A] text-[10px] uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-[#F9F9F6] transition-colors"
          >
            <Edit3 size={12} /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-4 space-y-3">
          <input value={draft.title} onChange={change("title")} data-testid={`admin-status-edit-title-${item.id}`} className="w-full bg-transparent border-b border-[#1A1A1A] py-2 font-serif text-xl" />
          <div className="grid grid-cols-2 gap-3">
            <select value={draft.status} onChange={change("status")} data-testid={`admin-status-edit-status-${item.id}`} className="bg-transparent border-b border-[#1A1A1A] py-2 text-sm">
              {["in-process", "active", "pending", "completed"].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <input
              type="number"
              min={0}
              max={100}
              value={draft.progress}
              onChange={change("progress")}
              data-testid={`admin-status-edit-progress-${item.id}`}
              className="bg-transparent border-b border-[#1A1A1A] py-2 text-sm"
              placeholder="Progress %"
            />
          </div>
          <input
            value={draft.target_date || ""}
            onChange={change("target_date")}
            data-testid={`admin-status-edit-target-${item.id}`}
            className="w-full bg-transparent border-b border-[#1A1A1A] py-2 text-sm"
            placeholder="Target date (e.g. Q3 2026)"
          />
          <textarea
            rows={3}
            value={draft.note || ""}
            onChange={change("note")}
            data-testid={`admin-status-edit-note-${item.id}`}
            className="w-full border border-[#E5E5E0] p-2 text-sm"
          />
          <button
            type="button"
            onClick={() => setEditing(null)}
            data-testid={`admin-status-cancel-${item.id}`}
            className="text-[10px] uppercase tracking-[0.2em] text-[#666666] hover:text-[#1A1A1A]"
          >
            Cancel
          </button>
          {error && <p className="text-xs text-[#5C2018]">{error}</p>}
        </div>
      ) : (
        <>
          <h3 className="font-serif text-xl md:text-2xl tracking-tight mt-3">{item.title}</h3>
          <p className="text-sm text-[#666666] mt-2 leading-relaxed">{item.note}</p>
          <div className="mt-5">
            <div className="h-px w-full bg-[#E5E5E0] relative">
              <div className="absolute inset-y-0 left-0 bg-[#5C2018]" style={{ width: `${item.progress || 0}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-[#666666]">
              <span>{item.progress || 0}% · {item.status}</span>
              {item.target_date && <span>{item.target_date}</span>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ----------------- INVESTORS -----------------
function InvestorsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/investors").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/investors/${id}/${action}`);
      load();
    } catch {
      // ignore
    }
  };

  return (
    <div data-testid="admin-investors-panel">
      {loading ? <p className="text-sm text-[#666666]">Loading…</p> : (
        items.length === 0 ? (
          <EmptyBox icon={Users} title="No investor applications yet" body="When investors register at /register their accounts appear here for approval." />
        ) : (
          <div className="border-t border-[#1A1A1A]">
            {items.map((u) => (
              <div key={u.id} data-testid={`admin-investor-${u.id}`} className="grid grid-cols-12 gap-4 py-5 border-b border-[#E5E5E0] items-center">
                <div className="col-span-12 md:col-span-4">
                  <p className="font-serif text-lg tracking-tight">{u.name}</p>
                  <p className="text-xs text-[#666666]">{u.email}</p>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm">
                  <p>{u.organization || "."}</p>
                  <p className="text-xs text-[#666666]">{u.investor_type || ""}</p>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
                    u.status === "approved" ? "border-[#5C2018] text-[#5C2018]" : u.status === "rejected" ? "border-[#666666] text-[#666666]" : "border-[#1A1A1A] text-[#1A1A1A]"
                  }`}>{u.status}</span>
                </div>
                <div className="col-span-12 md:col-span-3 flex md:justify-end gap-2">
                  {u.status !== "approved" && (
                    <button onClick={() => act(u.id, "approve")} data-testid={`admin-approve-${u.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#5C2018] text-[#F9F9F6] text-[10px] uppercase tracking-[0.2em] hover:bg-[#1A1A1A]"><Check size={12} /> Approve</button>
                  )}
                  {u.status !== "rejected" && (
                    <button onClick={() => act(u.id, "reject")} data-testid={`admin-reject-${u.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#1A1A1A] text-[10px] uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-[#F9F9F6]"><X size={12} /> Reject</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ----------------- INQUIRIES -----------------
function InquiriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/inquiries").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="admin-inquiries-panel">
      {loading ? <p className="text-sm text-[#666666]">Loading…</p> : (
        items.length === 0 ? (
          <EmptyBox icon={Mail} title="No investor inquiries yet" body="Inquiries submitted from the investor portal will appear here." />
        ) : (
          <div className="space-y-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {items.map((i) => (
              <div key={i.id} data-testid={`admin-inquiry-${i.id}`} className="bg-[#F9F9F6] p-6">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-serif text-lg">{i.name}</p>
                    <p className="text-xs text-[#666666]">{i.email} · {i.organization || "."}</p>
                  </div>
                  <p className="text-xs text-[#666666] inline-flex items-center gap-1"><Clock size={11} /> {new Date(i.created_at).toLocaleString()}</p>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <span><b>Type:</b> {i.investor_type || "."}</span>
                  <span><b>Capital:</b> {i.capital_range || "."}</span>
                </div>
                {i.message && <p className="mt-3 text-sm leading-relaxed">{i.message}</p>}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ----------------- PARTNERS -----------------
function PartnersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/partners").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="admin-partners-panel">
      {loading ? <p className="text-sm text-[#666666]">Loading…</p> : (
        items.length === 0 ? (
          <EmptyBox icon={Briefcase} title="No partner applications yet" body="Submissions from /partner appear here for review." />
        ) : (
          <div className="space-y-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {items.map((p) => (
              <div key={p.id} data-testid={`admin-partner-${p.id}`} className="bg-[#F9F9F6] p-6">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-serif text-lg">{p.company_name}</p>
                    <p className="text-xs text-[#666666]">{p.contact_name} · {p.email}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.22em] border border-[#5C2018] text-[#5C2018] px-2 py-1">{p.partner_type}</span>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <span><b>Country:</b> {p.country || "."}</span>
                  <span><b>Phone:</b> {p.phone || "."}</span>
                  <span><b>Volume:</b> {p.annual_volume || "."}</span>
                </div>
                {p.capabilities && <p className="mt-3 text-sm"><b>Capabilities:</b> {p.capabilities}</p>}
                {p.message && <p className="mt-2 text-sm leading-relaxed">{p.message}</p>}
                <p className="mt-3 text-xs text-[#666666]">{new Date(p.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function EmptyBox({ icon: Icon, title, body }) {
  return (
    <div className="border border-dashed border-[#E5E5E0] p-10 text-center">
      <Icon className="mx-auto text-[#5C2018]" />
      <p className="font-serif text-2xl mt-4 tracking-tight">{title}</p>
      <p className="text-sm text-[#666666] mt-2 max-w-md mx-auto">{body}</p>
      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#666666] inline-flex items-center gap-1">Live feed <ChevronRight size={12} /></p>
    </div>
  );
}
